import type { FC } from 'react';

import { cn } from '@crehana/compass.ui/v2';
import { useTranslation } from '@crehana/i18n';
import CloseSmall from '@crehana/icons/outline/CloseSmall';
import CheckedUser from '@crehana/icons/pictograms/CheckedUser';
import {
  Avatar,
  Chip,
  colors,
  Drawer,
  Loader,
  PrimaryButton,
  Snackbar,
  // biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
} from '@crehana/ui';

import SearchPaginateInput from '../SearchPaginateInput';
import {
  BeautyButton,
  ContentWrapper,
  FooterWrapper,
  HeaderWrapper,
  WrapperOption,
} from './AddUserCourseDrawer.styles';
import { useUserCourse } from './hooks';
import type { LabelMeta, User } from './types';

const { baseLighter, white } = colors;

interface AddUserCourseDrawerProps {
  courseId: string;
  organization: string;
  onSuccess?: (isSuccess: boolean) => void;
  isDark?: boolean;
  isOpen: boolean;
  handleDrawer: (isOpen: boolean) => void;
}

const AddUserCourseDrawer: FC<
  React.PropsWithChildren<AddUserCourseDrawerProps>
> = ({ courseId, organization, onSuccess, isDark, isOpen, handleDrawer }) => {
  const { t } = useTranslation();

  const {
    addUserCourse,
    loadUsers,
    isLoading,
    isSuccess,
    onChange,
    selectedUsers,
    snackbar,
    setSnackbar,
    removeUser,
  } = useUserCourse({
    courseId,
    isOpen,
    organization,
    onSuccess,
  });

  const Option = (option: User, labelMeta: LabelMeta) => (
    <WrapperOption isValue={labelMeta.context === 'value'}>
      <Avatar src={option.profilePictureUrl} size={24} />
      <p
        className={`font-body4 ml-8 text-${
          isDark ? 'gray-main' : 'base-lighter'
        }`}
      >
        {labelMeta.context === 'value'
          ? `${option.value}`
          : `${option.value} <${option.email}>`}
      </p>
    </WrapperOption>
  );

  const Loading = () => (
    <div className="w-full h-full flex items-center justify-center">
      <Loader isWhite={isDark} />
    </div>
  );

  const Success = () => (
    <>
      <HeaderWrapper className="justify-end" isDark={isDark}>
        {/* TODO: talk with the code owner to fix this code so we can enable this rule again */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <div className="cursor-pointer" onClick={() => handleDrawer(false)}>
          <CloseSmall color={isDark ? white : baseLighter} />
        </div>
      </HeaderWrapper>
      <ContentWrapper className="items-center justify-center text-center">
        <CheckedUser size={48} className="mb-20" color={baseLighter} />
        <p className="font-h4 mb-4">
          {t('USER_COURSE_DRAWER_DONE_TITLE_1')}
          <span
            className={cn(
              isDark ? 'text-secondary-main' : 'text-primary-main',
              'mx-8',
            )}
          >
            {selectedUsers.length}
          </span>
          {t('USER_COURSE_DRAWER_DONE_TITLE_2')}
        </p>
        <p className="font-body3 mb-24">
          {t('USER_COURSE_DRAWER_DONE_SUBTITLE')}
        </p>
        <PrimaryButton
          isDark={isDark}
          onClick={() => handleDrawer(false)}
          label={t('USER_COURSE_DRAWER_DONE_BUTTON')}
        />
      </ContentWrapper>
    </>
  );

  const titleColor = `text-${isDark ? 'white' : 'base-dark'}`;
  const mainColor = `text-${isDark ? 'white' : 'base-main'}`;
  const secondaryColor = `text-${isDark ? 'base-lighter' : 'gray-dark'}`;

  return (
    <Drawer
      open={isOpen}
      className="flex flex-col h-full"
      onDismiss={() => handleDrawer(false)}
      width={{ xs: '100vw', sm: '70vw', md: '45vw', lg: '35vw' }}
      isDark={isDark}
    >
      {isLoading ? (
        <Loading />
      ) : isSuccess ? (
        <Success />
      ) : (
        <>
          <HeaderWrapper hasLine className="justify-between" isDark={isDark}>
            <p className={`font-h4 ${titleColor}`}>
              {t('USER_COURSE_DRAWER_TITLE')}
            </p>
            {/* TODO: talk with the code owner to fix this code so we can enable this rule again */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
            <div className="cursor-pointer" onClick={() => handleDrawer(false)}>
              <CloseSmall color={isDark ? white : baseLighter} />
            </div>
          </HeaderWrapper>
          <ContentWrapper>
            <p className={`font-body4 ${titleColor} mb-24`}>
              {t('USER_COURSE_DRAWER_TITLE_2')}
            </p>
            <p className={`font-subtitle3 ${mainColor} mb-8`}>
              {t('USER_COURSE_DRAWER_HINT_1')}
            </p>
            <p className={`font-body4 ${secondaryColor} mb-8`}>
              {t('USER_COURSE_DRAWER_HINT_2')}
            </p>
            <SearchPaginateInput
              id="react-select-users-input"
              isMulti
              isSearchable
              isDark={isDark}
              onChange={onChange}
              isClearable={false}
              value={selectedUsers}
              loadOptions={loadUsers}
              formatOptionLabel={Option}
              controlShouldRenderValue={false}
              placeholder={t('USER_COURSE_DRAWER_PLACEHOLDER')}
              noResultsLabel={t('USER_COURSE_DRAWER_NOT_FOUND')}
            />
            <div className="flex flex-wrap py-36" style={{ gap: '10px' }}>
              {selectedUsers.map((user, i) => (
                <Chip
                  className="h-36"
                  isDark={isDark}
                  key={user.originalId}
                  onDelete={() => removeUser(i)}
                  content={
                    <>
                      <Avatar src={user.profilePictureUrl} size={24} />
                      <p
                        className={`font-body4 ml-8 text-${
                          isDark ? 'gray-main' : 'base-lighter'
                        }`}
                      >
                        {user.value || user.email}
                      </p>
                    </>
                  }
                />
              ))}
            </div>
          </ContentWrapper>
          <FooterWrapper isDark={isDark}>
            <BeautyButton
              className="font-button"
              onClick={() => handleDrawer(false)}
            >
              {t('USER_COURSE_DRAWER_CANCEL')}
            </BeautyButton>
            <PrimaryButton
              type="submit"
              isDark={isDark}
              onClick={() => addUserCourse()}
              disabled={!selectedUsers.length}
              label={t('USER_COURSE_DRAWER_ASSIGN')}
            />
          </FooterWrapper>
        </>
      )}
      <Snackbar
        {...snackbar}
        position="top-right"
        dismissOnDelay={2500}
        onDismiss={() => setSnackbar({})}
      />
    </Drawer>
  );
};

export default AddUserCourseDrawer;
